import axios from "axios";
import { useState } from "react";
import {
    API_ENDPOINT,
    getAuthHeaders,
    productCache,
    orderCache,
    orderIdCache,
    invoiceCache,
    pendingRequests
} from "./shared";

export default function useCatalogApi() {
    const [product, setProduct] = useState({});
    const [products, setProducts] = useState([]);
    const [similar, setSimilar] = useState([]);
    const [orders, setOrders] = useState([]);
    const [order, setOrder] = useState({});
    const [favorite, setFavorite] = useState({});
    const [favoriteProducts, setFavoriteProducts] = useState({});
    const [brands, setBrands] = useState([]);
    const [categories, setCategories] = useState({});
    const [phenotypes, setPhenotypes] = useState([]);
    const [tastes, setTastes] = useState([]);
    const [terpines, setTerpines] = useState([]);
    const [effects, setEffects] = useState([]);
    const [deliveries, setDeliveries] = useState([]);
    const [delivery, setDelivery] = useState({});
    const [openInvoices, setOpenInvoices] = useState([]);
    const [updating, setUpdating] = useState(false);

    return {
        product,
        products,
        setProducts,
        similar,
        orders,
        order,
        favorite,
        favoriteProducts,
        brands,
        setBrands,
        categories,
        phenotypes,
        tastes,
        terpines,
        effects,
        deliveries,
        delivery,
        openInvoices,
        setOpenInvoices,
        updating,
        setUpdating,

        getProducts(
            availability,
            brands,
            categories,
            subCategories,
            tastes,
            terpines,
            phenotypes,
            effects,
            sort,
            pageNum,
            pageLimit
        ) {
            return new Promise((resolve, reject) => {
                axios
                    .get(
                        `${API_ENDPOINT}/collections?page=${pageNum}&size=${pageLimit}&brands=${encodeURIComponent(brands.toString())}&categories=${categories.toString()}&subcategories=${subCategories.toString()}&tastes=${tastes}&terpines=${terpines}&phenotypes=${phenotypes}&effects=${effects}&sort=${sort}&availability=${availability}`,
                        { headers: getAuthHeaders() }
                    )
                    .then((response) => {
                        resolve(response.data);
                        const actualProducts =
                            pageNum === 1
                                ? response.data.products
                                : [...products, ...response.data.products];
                        setProducts(actualProducts);
                        setCategories(response.data.categories);
                        setPhenotypes(response.data.phenotypes);
                        setTerpines(response.data.terpines);
                        setTastes(response.data.tastes);
                        setEffects(response.data.effects);
                    })
                    .catch((error) => {
                        reject(error.response.data);
                    });
            });
        },
        getProductsCategories() {
            const url = `${API_ENDPOINT}/productCategories`;

            // Return cached data if available
            if (Object.keys(categories).length > 0) {
                return Promise.resolve({ categories, phenotypes });
            }

            if (pendingRequests.has(url)) {
                return pendingRequests.get(url);
            }

            const promise = new Promise((resolve, reject) => {
                axios
                    .get(url, { headers: getAuthHeaders() })
                    .then((response) => {
                        setCategories(response.data.categories);
                        setPhenotypes(response.data.phenotypes);
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error.response?.data || error);
                    })
                    .finally(() => {
                        pendingRequests.delete(url);
                    });
            });

            pendingRequests.set(url, promise);
            return promise;
        },
        getProductsByAvailability(availability) {
            return new Promise((resolve, reject) => {
                axios
                    .get(`${API_ENDPOINT}/${availability}`, {
                        headers: getAuthHeaders()
                    })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error.response.data);
                    });
            });
        },
        getNewRelease() {
            return new Promise((resolve, reject) => {
                axios
                    .get(`${API_ENDPOINT}/new-release`, {
                        headers: getAuthHeaders()
                    })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error.response.data);
                    });
            });
        },
        getFastMovers() {
            return new Promise((resolve, reject) => {
                axios
                    .get(`${API_ENDPOINT}/fast-movers`, {
                        headers: getAuthHeaders()
                    })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error.response.data);
                    });
            });
        },
        getProductsByTitle(title) {
            return new Promise((resolve, reject) => {
                axios
                    .get(`${API_ENDPOINT}/collections/title/${title}`, {
                        headers: getAuthHeaders()
                    })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error.response.data);
                    });
            });
        },
        getProduct(id) {
            return new Promise((resolve, reject) => {
                // Return cached data if available for instant response
                if (productCache[id]) {
                    setProduct(productCache[id].product);
                    setSimilar(productCache[id].similarProducts);
                    resolve(productCache[id]);
                    // We still re-fetch in the background to ensure data is fresh (Stale-While-Revalidate pattern)
                }

                axios
                    .get(`${API_ENDPOINT}/products/${id}`, {
                        headers: getAuthHeaders()
                    })
                    .then((response) => {
                        setProduct(response.data.product);
                        setSimilar(response.data.similarProducts);
                        productCache[id] = response.data;
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error.response?.data || error.message);
                    });
            });
        },
        getProductInventory(id) {
            return new Promise((resolve, reject) => {
                axios
                    .get(`${API_ENDPOINT}/products/handle/${id}`, {
                        headers: getAuthHeaders()
                    })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error.response.data);
                    });
            });
        },
        getFavorite(id) {
            return new Promise((resolve, reject) => {
                axios
                    .get(`${API_ENDPOINT}/favorites/${id}`, {
                        headers: getAuthHeaders()
                    })
                    .then((response) => {
                        resolve(response.data);
                        setFavorite(response.data.favorite);
                        setFavoriteProducts(response.data.favoriteProducts);
                    })
                    .catch((error) => {
                        reject(error.response.data);
                        setFavorite({});
                        setFavoriteProducts({});
                    });
            });
        },
        addFavorite(data) {
            return new Promise((resolve, reject) => {
                axios
                    .post(`${API_ENDPOINT}/favorites`, data, {
                        headers: getAuthHeaders()
                    })
                    .then((response) => {
                        resolve(response.data);
                        setFavorite(response.data.savedFavorite);
                        setFavoriteProducts(response.data.favoriteProducts);
                    })
                    .catch((error) => {
                        reject(error.response.data);
                    });
            });
        },
        updateFavorite(id, data) {
            return new Promise((resolve, reject) => {
                axios
                    .put(`${API_ENDPOINT}/favorites/${id}`, data, {
                        headers: getAuthHeaders()
                    })
                    .then((response) => {
                        resolve(response.data);
                        setFavorite(response.data.updatedFavorite);
                        setFavoriteProducts(response.data.favoriteProducts);
                    })
                    .catch((error) => {
                        reject(error.response.data);
                    });
            });
        },
        getOrdersByCustomerId(id) {
            return new Promise((resolve, reject) => {
                axios
                    .get(`${API_ENDPOINT}/orders/${id}`, {
                        headers: getAuthHeaders()
                    })
                    .then((response) => {
                        resolve(response.data);
                        setOrders(response.data.orders);
                    })
                    .catch((error) => {
                        reject(error.response.data);
                    });
            });
        },
        getOrder(id) {
            const url = `${API_ENDPOINT}/orders/order/${id}`;

            if (orderIdCache[id]) {
                return Promise.resolve({ order: orderIdCache[id] });
            }

            if (pendingRequests.has(url)) {
                return pendingRequests.get(url);
            }

            const promise = new Promise((resolve, reject) => {
                axios
                    .get(url, { headers: getAuthHeaders() })
                    .then((response) => {
                        if (response.data.order) {
                            const orderData = response.data.order;
                            orderIdCache[id] = orderData;
                            if (orderData.OrderNbr?.value) {
                                orderCache[orderData.OrderNbr.value] = orderData;
                            }
                        }
                        resolve(response.data);
                        setOrder(response.data.order);
                    })
                    .catch((error) => {
                        reject(error.response?.data || error);
                    })
                    .finally(() => {
                        pendingRequests.delete(url);
                    });
            });

            pendingRequests.set(url, promise);
            return promise;
        },
        getOrderByOrderNumber(orderNumber) {
            const url = `${API_ENDPOINT}/orders/orderNumber/${orderNumber}`;

            if (orderCache[orderNumber]) {
                return Promise.resolve({ order: orderCache[orderNumber] });
            }

            if (pendingRequests.has(url)) {
                return pendingRequests.get(url);
            }

            const promise = new Promise((resolve, reject) => {
                axios
                    .get(url, { headers: getAuthHeaders() })
                    .then((response) => {
                        if (response.data.order) {
                            orderCache[orderNumber] = response.data.order;
                        }
                        resolve(response.data);
                        setOrder(response.data.order);
                    })
                    .catch((error) => {
                        reject(error.response?.data || error);
                    })
                    .finally(() => {
                        pendingRequests.delete(url);
                    });
            });

            pendingRequests.set(url, promise);
            return promise;
        },
        getBanners() {
            return new Promise((resolve, reject) => {
                axios
                    .get(`${API_ENDPOINT}/contents/banners`, {
                        headers: getAuthHeaders()
                    })
                    .then((response) => {
                        resolve(response.data);
                    })
            });
        },
        getContents() {
            return new Promise((resolve, reject) => {
                axios
                    .get(`${API_ENDPOINT}/contents`, {
                        headers: getAuthHeaders()
                    })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error.response.data);
                    });
            });
        },
        getDeliveries() {
            return new Promise((resolve, reject) => {
                axios
                    .get(`${API_ENDPOINT}/delivery`, {
                        headers: getAuthHeaders()
                    })
                    .then((response) => {
                        resolve(response.data);
                        setDeliveries(response.data.deliveries);
                    })
                    .catch((error) => {
                        reject(error.response.data);
                    });
            });
        },
        getDelivery(id) {
            return new Promise((resolve, reject) => {
                axios
                    .get(`${API_ENDPOINT}/delivery/${id}`, {
                        headers: getAuthHeaders()
                    })
                    .then((response) => {
                        resolve(response.data);
                        setDelivery(response.data.delivery);
                    })
                    .catch((error) => {
                        reject(error.response.data);
                    });
            });
        },
        addDelivery(data) {
            return new Promise((resolve, reject) => {
                axios
                    .post(`${API_ENDPOINT}/delivery`, data, {
                        headers: getAuthHeaders()
                    })
                    .then((response) => {
                        resolve(response.data);
                        setDelivery(response.data.savedDelivery);
                    })
                    .catch((error) => {
                        reject(error.response.data);
                    });
            });
        },
        getSimilarProducts(productId) {
            return new Promise((resolve, reject) => {
                axios
                    .get(`${API_ENDPOINT}/${productId}/similiarProducts`, {
                        headers: getAuthHeaders()
                    })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error.response.data);
                    });
            });
        },
        updateDelivery(id, data) {
            return new Promise((resolve, reject) => {
                axios
                    .put(`${API_ENDPOINT}/delivery/${id}`, data, {
                        headers: getAuthHeaders()
                    })
                    .then((response) => {
                        resolve(response.data);
                        setDelivery(response.data.updatedDelivery);
                    })
                    .catch((error) => {
                        reject(error.response.data);
                    });
            });
        },
        getCutoff() {
            return new Promise((resolve, reject) => {
                axios
                    .get(`${API_ENDPOINT}/delivery/cutoff/update`, {
                        headers: getAuthHeaders()
                    })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error.response.data);
                    });
            });
        },
        updateCutoff(data) {
            return new Promise((resolve, reject) => {
                axios
                    .put(`${API_ENDPOINT}/delivery/cutoff/update`, data, {
                        headers: getAuthHeaders()
                    })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error.response.data);
                    });
            });
        },
        removeDelivery(id) {
            return new Promise((resolve, reject) => {
                axios
                    .delete(`${API_ENDPOINT}/delivery/${id}`, {
                        headers: getAuthHeaders()
                    })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error.response.data);
                    });
            });
        },
        getAllBrands() {
            const url = `${API_ENDPOINT}/brands`;
            if (pendingRequests.has(url)) {
                return pendingRequests.get(url);
            }

            const promise = new Promise((resolve, reject) => {
                axios
                    .get(url, { headers: getAuthHeaders() })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error.response?.data || error);
                    })
                    .finally(() => {
                        pendingRequests.delete(url);
                    });
            });

            pendingRequests.set(url, promise);
            return promise;
        },
        addPickUpPayment(data) {
            return new Promise((resolve, reject) => {
                axios
                    .post(`${API_ENDPOINT}/pickup-payment`, data, {
                        headers: getAuthHeaders()
                    })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error.response?.data || error);
                    });
            });
        },
        getAllDetailsOfProducts() {
            return new Promise((resolve, reject) => {
                axios
                    .get(`${API_ENDPOINT}/alldetailsOfProducts`, {
                        headers: getAuthHeaders()
                    })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error.response.data);
                    });
            });
        },
        getOpenInvoices(customerId) {
            return new Promise((resolve, reject) => {
                axios
                    .get(`${API_ENDPOINT}/open-invoices/get-open-invoices-by-customer/${customerId}`, {
                        headers: getAuthHeaders()
                    })
                    .then((response) => {
                        resolve(response.data);
                        setOpenInvoices(response.data.openInvoices);
                    })
                    .catch((error) => {
                        reject(error.response.data);
                    });
            });
        },
        getOpenInvoiceByInvoiceId(invoiceId) {
            const url = `${API_ENDPOINT}/open-invoices/get-open-invoice-by-id/${invoiceId}`;

            if (invoiceCache[invoiceId]) {
                return Promise.resolve(invoiceCache[invoiceId]);
            }

            if (pendingRequests.has(url)) {
                return pendingRequests.get(url);
            }

            const promise = new Promise((resolve, reject) => {
                axios
                    .get(url, { headers: getAuthHeaders() })
                    .then((response) => {
                        invoiceCache[invoiceId] = response.data;
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error.response?.data || error);
                    })
                    .finally(() => {
                        pendingRequests.delete(url);
                    });
            });

            pendingRequests.set(url, promise);
            return promise;
        },
        updateOrCreateContent(data) {
            return new Promise((resolve, reject) => {
                axios
                    .post(`${API_ENDPOINT}/contents`, data, {
                        headers: getAuthHeaders()
                    })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error.response.data);
                    });
            });
        },
        getContentById(id) {
            return new Promise((resolve, reject) => {
                axios
                    .get(`${API_ENDPOINT}/contents/${id}`, {
                        headers: getAuthHeaders()
                    })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error.response.data);
                    });
            });
        },
        getAllCustomer() {
            return new Promise((resolve, reject) => {
                axios
                    .get(`${API_ENDPOINT}/customers`, {
                        headers: getAuthHeaders()
                    })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error.response.data);
                    });
            });
        },
        deleteContent(contentId) {
            return new Promise((resolve, reject) => {
                axios
                    .delete(`${API_ENDPOINT}/contents/${contentId}`, {
                        headers: getAuthHeaders()
                    })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error.response.data);
                    });
            });
        },
        getSpotLightProduct() {
            return new Promise((resolve, reject) => {
                axios
                    .get(`${API_ENDPOINT}/contents/spotlight`, {
                        headers: getAuthHeaders()
                    })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error.response.data);
                    });
            });
        },
        getProductByContent(contentId) {
            return new Promise((resolve, reject) => {
                axios
                    .get(`${API_ENDPOINT}/contents/product-by-content/${contentId}`, {
                        headers: getAuthHeaders()
                    })
                    .then((response) => {
                        resolve(response.data);
                        setProducts(response.data.products);
                    })
                    .catch((error) => {
                        reject(error.response.data);
                    });
            });
        },
        reschedulePaymentPickup(id, data) {
            return new Promise((resolve, reject) => {
                axios
                    .put(`${API_ENDPOINT}/pickup-payment/${id}`, data, {
                        headers: getAuthHeaders()
                    })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error.response?.data || error);
                    });
            });
        }
    };
}
